// api.ts
import { ReviewResult } from './types';

const API_BASE = process.env.REACT_APP_API_URL || '/contract';

// ── Auth helpers ──

const TOKEN_KEY = 'paxar_token';
const USER_KEY = 'paxar_username';

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function getUsername(): string {
  return localStorage.getItem(USER_KEY) || '';
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

// ── Auth API ──

export async function login(username: string, password: string) {
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || '登录失败');
  }
  const data = await response.json();
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(USER_KEY, data.username);
  return data;
}

export async function register(username: string, password: string) {
  const response = await fetch(`${API_BASE}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || '注册失败');
  }
  const data = await response.json();
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(USER_KEY, data.username);
  return data;
}

export async function logout() {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: authHeaders(),
    });
  } catch {
    // ignore network errors during logout
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export async function getMe() {
  const response = await fetch(`${API_BASE}/api/auth/me`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error('Not authenticated');
  }
  return response.json();
}

// ── Review API ──

export async function uploadAndReview(
  file: File,
  reviewStandard: string = 'standard'
): Promise<ReviewResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('review_standard', reviewStandard);

  const response = await fetch(`${API_BASE}/api/review/file`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Review failed');
  }

  return response.json();
}

export async function getContractTypes() {
  const response = await fetch(`${API_BASE}/api/review/types`);
  if (!response.ok) {
    throw new Error('Failed to fetch contract types');
  }
  return response.json();
}

// ── History API ──

export async function getHistory(limit: number = 20, offset: number = 0) {
  const response = await fetch(
    `${API_BASE}/api/history?limit=${limit}&offset=${offset}`,
    { headers: authHeaders() }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch history');
  }
  return response.json();
}

export async function getHistoryDetail(id: string) {
  const response = await fetch(`${API_BASE}/api/history/${id}`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch review detail');
  }
  return response.json();
}

export async function deleteHistory(id: string) {
  const response = await fetch(`${API_BASE}/api/history/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to delete review');
  }
  return response.json();
}